import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardText,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Link } from "react-router-dom";
import { set } from "lodash";
import {recommendationData} from "../../common/data/demo/recommendations/recommendations";

const EmptyState = () => (
  <Card>
    <div className="text-center p-5">
      <div className="mb-4">
        <i className="mdi mdi-file-search text-primary" style={{ fontSize: "4rem" }} />
      </div>
      <h3 className="mb-3 font-weight-bold">No Recommendations Available Yet</h3>
      <p className="text-muted mb-4">
        It looks like there are no recommendations available at the moment. This could be due to recent activity or pending data updates.
        To gain recommendations and enhance your security.
      </p>

      <div className="d-flex justify-content-center gap-3">
        <Link to="/vulnerabilityscan" className="btn btn-primary px-4 py-2">
          Start a Vulnerability Scan
        </Link>
        <Link to="/malwareAnalysis" className="btn btn-warning px-4 py-2">
          Run Malware Analysis
        </Link>
        <Link to="/tm-create" className="btn btn-success px-4 py-2">
          Begin AI Threat Modeling
        </Link>
      </div>
    </div>
  </Card>
);

const selectSevirityColor = (severity) => {
  switch (severity) {
    case "W":
    case "M":
      return "warning";
    case "I":
    case "L":
      return "info";
    case "D":
    case "C":
      return "danger";
    case "H":
    case "O":
      return "orange";
  }
};

const processTextWithCode = (text) => {
  // Handle multiline code blocks (```language ... ``` or just ``` ... ```)
  const codeBlockPattern = /```([a-zA-Z]*)\n([\s\S]*?)```/g;

  text = text.replace(codeBlockPattern, (match, language, codeContent) => {
    // Escape special HTML characters within the code block
    const escapedContent = codeContent
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Preserve line breaks
    const formattedContent = escapedContent.replace(/\n/g, '<br/>');

    // Add the language class only if a language is specified
    const languageClass = language ? ` class="language-${language}"` : '';

    return `<pre><code${languageClass}>${formattedContent}</code></pre>`;
  });

  // Handle inline code (`text`)
  const inlineCodePattern = /`([^`\n]+?)`/g;

  text = text.replace(inlineCodePattern, (match, codeContent) => {
    // Escape special HTML characters within inline code
    const escapedContent = codeContent
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return `<code>${escapedContent}</code>`;
  });

  return text;
};


const RecommendationCard = ({ recommendation, onClick }) => (
  <Card
    outline
    color={selectSevirityColor(recommendation.severityLetter)}
    className="border h-100" // Added h-100 for full height
    onClick={onClick}
    style={{ cursor: "pointer" }}
  >
    <CardHeader className="bg-transparent">
      <h5 className={`my-0 text-${selectSevirityColor(recommendation.severityLetter)}`}>
        <i className={`${recommendation.icon} me-3`} />
        {recommendation.title}
      </h5>
    </CardHeader>
    <CardBody className="d-flex flex-column">
      <CardText className="flex-grow-1" style={{
        overflow: "hidden",
        display: "-webkit-box",
        WebkitLineClamp: "4",
        WebkitBoxOrient: "vertical",
        textOverflow: "ellipsis"
      }}>
        {recommendation.content}
      </CardText>
    </CardBody>
  </Card>
);

const RecommendationDetails = ({ selectedRecommendation }) => {
  return (
    <div>
  <h5 className="mb-3">{selectedRecommendation.content}</h5>
  <div className="recommendation-content">
    {selectedRecommendation.detailedDescription?.split('\n').map((line, index) => {
      if (!line.trim()) return null; // Skip empty lines

      // Determine line types
      const isNumberedList = /^\d+\.\s/.test(line);
      const isBoldLine = /^\s*\*\*(.*?)\*\*\s*$/.test(line.trim());
      const isBulletPoint = /^\s*\*+\s/.test(line);

      // Function to process bold text
      const processBold = (text) => {
        return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      };

      // Preprocess line for escaping and inline code
      let processedLine = processTextWithCode(line);

      // Apply bold formatting
      processedLine = processBold(processedLine);

      // Handle bold lines
      if (isBoldLine) {
        const content = processedLine.replace(/^\s*\*\*(.*?)\*\*\s*$/, '$1');
        return (
          <div key={index} className="mb-2" style={{ fontWeight: 'bold' }}>
            <span dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        );
      }

      // Handle bullet points
      if (isBulletPoint) {
        const indentLevel = (line.match(/^\s*\*+/)[0].match(/\*/g) || []).length;
        const content = processedLine.replace(/^\s*\*+\s*/, '');
        return (
          <div
            key={index}
            className="d-flex mb-2"
            style={{ marginLeft: `${(indentLevel - 1) * 20}px` }}
          >
            <div className="me-2">•</div>
            <div dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        );
      }

      // Handle numbered lists
      if (isNumberedList) {
        const number = line.match(/^\d+/)[0];
        const content = processedLine.replace(/^\d+\.\s/, '');
        return (
          <div key={index} className="d-flex mb-3">
            <div className="me-2">{number}.</div>
            <div dangerouslySetInnerHTML={{ __html: content }} />
          </div>
        );
      }

      // Render plain text or other content
      return (
        <div
          key={index}
          className="mb-2"
          dangerouslySetInnerHTML={{ __html: processedLine }}
        />
      );
    })}
  </div>
  <div className="text-end mt-4">
    <Button color="primary" onClick={() => setModal(false)}>
      Close
    </Button>
  </div>
</div>

  );
};

const Recommendations = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [modal, setModal] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username;

  const toggle = () => setModal(!modal);

  useEffect(() => {
    fetch(`https://api.compliance.cx/recommendations/${userId}`)
      .then((response) => response.json())
      .then((data) => setRecommendations(data))
      .catch((error) =>{
        console.error("Error fetching recommendations:", error)
        if (userId === "demo") {
        setRecommendations(recommendationData);
      }});
      
  }, [userId]);

  const handleCardClick = (recommendation) => {
    setSelectedRecommendation(recommendation);
    toggle();
  };

  // Filter out recommendations where severityLetter is "I"
  const filteredRecommendations = recommendations.filter(
    (recommendation) => recommendation.severityLetter !== "I" && recommendation.severityLetter !== "L"
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Breadcrumbs breadcrumbItem="Recommendations" />
        <Row className="g-4">
          {filteredRecommendations.length > 0 ? (
            filteredRecommendations.map((recommendation, index) => (
              <Col lg={4} md={6} sm={12} key={index}>
                <RecommendationCard
                  recommendation={recommendation}
                  onClick={() => handleCardClick(recommendation)}
                />
              </Col>
            ))
          ) : (
            <EmptyState />
          )}
        </Row>

        <Modal isOpen={modal} toggle={toggle} centered size="lg">
          <ModalHeader toggle={toggle}>
            {selectedRecommendation && (
              <h3 className={`text-${selectSevirityColor(selectedRecommendation.severityLetter)}`}>
                {selectedRecommendation.title}
              </h3>
            )}
          </ModalHeader>
          <ModalBody>
            {selectedRecommendation ? (
              <RecommendationDetails selectedRecommendation={selectedRecommendation} />
            ) : (
              <p>No details available.</p>
            )}
          </ModalBody>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default Recommendations;
